<template>
  <div>
    <v-row
      align="center"
      justify="center"
    >
      <v-col>
        <v-skeleton-loader
          v-if="loading"
          class="m-auto"
          type="table"
          transition="scale-transition"
        />
        <VTableRemote
          v-else
          ref="tableRemoteRef"
          :iconHeader="hasPermission('chartAction.add') ? 'add' : null"
          :items="displayedItems"
          :headers="tableHeaders"
          :pagination="pagination"
          :itemActions="getItemActions"
          :title="$t('administration.correctiveActions')"
          :centerColumns="['action']"
          :notSortableColumns="['action']"
          :sortableColumns="['name']"
          hideDefaultFooter
          :isCustomIcon="true"
          :isSearchable="false"
          @iconAction="onClickAction"
          @sendQueryParams="fetchQuery"
        >
          <template #withInfiniteScroll>
            <div v-intersect="endIntersect" />
          </template>
          <template #SearchWithFilters>
            <SearchWithFilters
              :filters="[]"
              :searchPlaceholder="$t('charts.searchBy')"
              :filterName="filterName"
              @onSearch="handleSearch"
            />
          </template>
        </VTableRemote>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Vue from 'vue';
import VTableRemote from '@/components/shared/content-views/VTableRemote';
import ConfirmForm from '@/components/shared/widgets/ConfirmForm.vue';
import SearchWithFilters from '@/components/shared/SearchWithFilters.vue';
import perpage from '@/constants/pagination';
import ChartForm from './ChartForm.vue';

export default {
    components: {
        VTableRemote,
        SearchWithFilters,
    },
    props: {
        isEdition: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            name: {},
            correctiveActions: [],
            pagination: {},
            queryParams: { perpage },
            filterName: 'correctiveActions',
            loading: false,
        };
    },
    computed: {
        ...mapGetters([
            'userLang',
            'teamMainLang',
            'selectedLang',
            'hasPermission',
        ]),
        tableHeaders() {
            return [
                {
                    text: this.$t('forms.labels.name'),
                    value: 'name',
                },
                {
                    text: this.$t('forms.labels.analysis'),
                    value: 'analysis',
                    sortable: false,
                },
            ];
        },
        displayedItems() {
            return this.correctiveActions.map(chart => ({
                id: chart.id,
                active: chart.active,
                name: chart.name[this.userLang] || chart.name[this.teamMainLang],
                analysis: chart.selectedAnalysis.map(analysis => analysis.name[this.userLang] || analysis.name[this.teamMainLang]).join(','),
            }));
        },
        getItemActions() {
            const actions = [
                {
                    name: this.$t('forms.btn.edit'),
                    icon: 'edit',
                    iconAndTextColor: '#26384B',
                },
                {
                    name: this.$t('forms.btn.delete'),
                    icon: 'delete',
                    iconAndTextColor: 'red',
                },
            ];
            return actions;
        },
    },
    watch: {
        data: {
            handler() {
                this.$emit('sendData', this.name);
            },
            deep: true,
        },
    },
    async created() {
        this.loading = true;
        await this.loadData();
        this.loading = false;
    },
    methods: {
        updateData() {
            this.$emit('sendData', this.name);
        },
        async onClickAction(item, action) {
            if (item === 'add' && !action) {
                this.onClickAdd();
            } else {
                switch (action.icon) {
                    case 'edit':
                        this.onClickEdit(item);
                        break;
                    case 'delete':
                        this.onClickDelete(item);
                        break;
                    default:
                        break;
                }
            }
        },
        async addChart(requestPayload) {
            await this.$rq.addChartAction(requestPayload);

            this.$toasted.global.toast_success({
                message: this.$t('msg.chartAdded'),
            });
            await this.loadData();
        },
        async editChart(chartId, requestPayload) {
            if (!requestPayload) return;
            await this.$rq.editChartAction(chartId, requestPayload);

            this.$toasted.global.toast_success({
                message: this.$t('msg.chartEdited'),
            });
            await this.loadData();
        },
        async deleteChart(chartId) {
            await this.$rq.deleteChartAction(chartId);

            this.$toasted.global.toast_success({
                message: this.$t('msg.chartRemoved'),
            });
            await this.loadData();
        },
        async loadData() {
            const { data } = await this.$rq.getChartsAction(this.queryParams);
            this.correctiveActions = [...data.charts];
            this.pagination = data.pagination;
        },
        onClickAdd() {
            this.$root.$emit('showModal', {
                title: this.$t('charts.add'),
                component: Vue.component('ChartForm', ChartForm),
                showLangs: true,
                onConfirm: requestPayload => this.addChart(requestPayload),
            });
        },
        onClickEdit(item) {
            const itemToEdit = { ...this.correctiveActions.find(el => el.id === item.id) };
            itemToEdit.selectedAnalysis = itemToEdit.selectedAnalysis.map((sA => sA.id));
            this.$root.$emit('showModal', {
                title: this.$t('charts.edit'),
                component: Vue.component('ChartForm', ChartForm),
                showLangs: true,
                payload: {
                    chart: itemToEdit,
                    isEdition: true,
                },
                onConfirm: requestPayload => this.editChart(item.id, requestPayload),
            });
        },
        onClickDelete(item) {
            this.$root.$emit('showModal', {
                title: this.$t('charts.delete'),
                component: Vue.component('ConfirmForm', ConfirmForm),
                payload: { msg: this.$t('charts.confirmRemove') },
                onConfirm: () => this.deleteChart(item.id),
                isForm: false,
            });
        },
        async fetchQuery({ order, ...params }) {
            this.queryParams = {
                ...this.queryParams,
                ...params,
                page: 1,
                order,
            };
            await this.loadData();
        },
        handleSearch(search) {
            this.fetchQuery({ search });
        },
        async endIntersect(_, __, isIntersecting) {
            const condition = this.pagination.currentPage < this.pagination.totalPageCount;
            if (isIntersecting && condition) {
                this.queryParams.page = this.pagination.currentPage + 1;
                await this.loadData();
                const ref = this.$refs?.tableRemoteRef?.$refs?.tableRemote;
                if (ref?.onScroll) {
                    ref.onScroll();
                }
            }
        },
    },
};
</script>
